import { RefObject, useEffect, useState } from 'react'

import { debounce } from 'lodash-es'

import { Selection } from 'src/utils/selection'

type SelectionDescriptor = {
    selectedText: string
    isWithinOuterElement: boolean
    relativeRect: {
        top: number
        bottom: number
        left: number
        right: number
    }
}

const DEFAULT_DESCRIPTOR: SelectionDescriptor = {
    selectedText: '',
    isWithinOuterElement: false,
    relativeRect: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
}

export const useSelection = (outerElementRef: RefObject<HTMLElement | null>) => {
    const [selectionDescriptor, setSelectionDescriptor] = useState<SelectionDescriptor>(DEFAULT_DESCRIPTOR)

    useEffect(() => {
        const onSelectionChange = debounce(() => {
            if (!outerElementRef.current) return

            const selection = Selection.get()!
            if (selection.rangeCount === 0) {
                setSelectionDescriptor(DEFAULT_DESCRIPTOR)

                return
            }

            const selectedText = Selection.getSelectableText(selection)

            const range = selection.getRangeAt(0)
            const isWithinOuterElement = outerElementRef.current.contains(range.commonAncestorContainer)

            const selectionBoundingRect = range.getBoundingClientRect()
            const outerElementBoundingRef = outerElementRef.current.getBoundingClientRect()
            const xOffset = outerElementRef.current.scrollLeft - outerElementBoundingRef.x
            const yOffset = outerElementRef.current.scrollTop - outerElementBoundingRef.y

            const relativeRect: SelectionDescriptor['relativeRect'] = {
                top: yOffset + selectionBoundingRect.top,
                bottom: yOffset + selectionBoundingRect.bottom,
                left: xOffset + selectionBoundingRect.left,
                right: xOffset + selectionBoundingRect.right,
            }

            setSelectionDescriptor({
                selectedText,
                isWithinOuterElement,
                relativeRect,
            })
        }, 200)
        document.addEventListener('selectionchange', onSelectionChange)

        return () => {
            document.removeEventListener('selectionchange', onSelectionChange)
        }
    }, [outerElementRef])

    return selectionDescriptor
}
